"use client";

import { useEffect, useState } from "react";

type TocItem = {
  id: string;
  label: string;
};

type ArticleTocProps = {
  containerId: string;
  titleId?: string;
};

const toSlug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

/** Reads h2/h3 headings from the container and assigns missing IDs. */
const readSectionItems = (containerId: string): TocItem[] => {
  const container = document.getElementById(containerId);
  if (!container) return [];

  const headings = Array.from(container.querySelectorAll("h2, h3"));
  const usedIds = new Set<string>();
  const sectionItems: TocItem[] = [];

  headings.forEach((heading) => {
    const label = heading.textContent?.trim();
    if (!label) return;

    let id = heading.id || toSlug(label);
    if (!id) return;

    let suffix = 2;
    const baseId = id;
    while (usedIds.has(id)) {
      id = `${baseId}-${suffix}`;
      suffix += 1;
    }

    usedIds.add(id);
    if (!heading.id) heading.id = id;
    sectionItems.push({ id, label });
  });

  return sectionItems;
};

export function ArticleToc({
  containerId,
  titleId = "article-title",
}: ArticleTocProps) {
  const [sectionItems, setSectionItems] = useState<TocItem[]>([]);
  const items = [{ id: titleId, label: "Overview" }, ...sectionItems];

  // Headings live in the DOM, so read them after mount (never during SSR).
  useEffect(() => {
    setSectionItems(readSectionItems(containerId));
  }, [containerId]);

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
  };

  return (
    <aside className="sticky top-24 hidden h-fit self-start flex-col gap-5 lg:flex lg:w-64 xl:w-72">
      <span className="text-foreground text-base font-medium">
        On this page
      </span>
      <nav className="flex flex-col gap-3">
        {items.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            onClick={(e) => handleScroll(e, item.id)}
            className="text-muted-foreground hover:text-foreground text-base"
          >
            {item.label}
          </a>
        ))}
      </nav>
    </aside>
  );
}
